import React, { useState, useContext } from 'react';
import { IconButton, Tooltip, DialogContentText, Select, MenuItem, Box, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { DocumentDataExclusionReasons, DocumentDataStatuses } from '../../../types/packager-types';
import {
  DocumentDataExclusionReasonDescriptions,
  formatExclusionReason,
} from '../../../types/packager-enum-descriptions';
import { useDocumentDataUpdate } from '../../hooks/documentData/useDocumentDataUpdate';
import { useQueryClient } from '@tanstack/react-query';
import DialogGlynt from '../../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../../common/Dialog/DialogActionsGlynt';
import { FormControlStyled, FormLabelStyled } from '../../common/Dialog/DialogGlynt';
import { AppContext } from '../../../App';
import { extractApiError } from '../../../utils/extractApiError';

export interface IPackageEntryStatusChangeProps {
  docDataId: string;
}

const DocumentDataStatusChange: React.FC<IPackageEntryStatusChangeProps> = ({ docDataId }) => {
  const { setToast } = useContext(AppContext);
  const queryClient = useQueryClient();
  const [pendingAction, setPendingAction] = useState<DocumentDataStatuses | null>(null);
  const [excludeDialogOpen, setExcludeDialogOpen] = useState(false);
  const [exclusionReason, setExclusionReason] = useState<DocumentDataExclusionReasons | ''>('');

  // FIXME: We need document data in package entry to correctly set this, and to toggle including or excluding
  const isIncluded = true;
  const isExcluded = false;

  if (!isIncluded && !isExcluded) return null;

  const title = isIncluded ? 'Exclude from Document Data' : 'Include in Document Data';

  const { mutate: updateDocumentData, isPending: isLoading } = useDocumentDataUpdate({
    onSuccess: () => {
      const message =
        pendingAction === DocumentDataStatuses.EXCLUDED
          ? 'Document data excluded'
          : 'Document data included';

      setToast({ variant: 'success', open: true, message });

      queryClient.invalidateQueries({
        queryKey: ['packageEntries']
      });
      // A source-data change can make the package stale.
      queryClient.invalidateQueries({ queryKey: ['packages'] });
      queryClient.invalidateQueries({ queryKey: ['package'] });
      setPendingAction(null);
    },
    onError: (error: any) => {
      setToast({
        variant: 'error',
        open: true,
        message: extractApiError(error, 'Failed to update document data status'),
      });
      console.error('Failed to update document data status:', error);
      setPendingAction(null);
    },
  });

  const handleClick = () => {
    if (isIncluded) {
      setExcludeDialogOpen(true);
      return;
    }

    setPendingAction(DocumentDataStatuses.INCLUDED);
  };

  const handleCloseExcludeDialog = () => {
    setExcludeDialogOpen(false);
    setExclusionReason('');
  };

  const handleConfirmExclude = () => {
    if (!exclusionReason) {
      setToast({ variant: 'error', open: true, message: 'Please select an exclusion reason' });
      return;
    }

    setPendingAction(DocumentDataStatuses.EXCLUDED);
    updateDocumentData({
      docDataId: docDataId,
      exclusionReason: exclusionReason as DocumentDataExclusionReasons,
    });
    handleCloseExcludeDialog();
  };

  return (
    <>
      <Tooltip title={title} placement="top">
        <span>
          <IconButton size="small" onClick={handleClick} aria-label={title} disabled={isLoading}>
            {isIncluded ? (
              <DeleteIcon fontSize="small" />
            ) : (
              <AddCircleOutlineIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
      <DialogGlynt
        open={excludeDialogOpen}
        closeModal={handleCloseExcludeDialog}
        cypressId="dialog-exclude-document-data"
        width="500px"
      >
        <DialogTitleGlynt title="Exclude Document Data" closeModal={handleCloseExcludeDialog} />
        <DialogContentGlynt>
          <DialogContentText sx={{ padding: '10px 3px' }}>
            <FormControlStyled fullWidth>
              <FormLabelStyled>EXCLUSION REASON *</FormLabelStyled>
              <Select
                value={exclusionReason}
                onChange={(e) => setExclusionReason(e.target.value as DocumentDataExclusionReasons)}
                variant="outlined"
                size="small"
                data-cy="exclusion-reason-select"
              >
                {Object.values(DocumentDataExclusionReasons).map((reason) => (
                  <MenuItem key={reason} value={reason}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.25 }}>
                      <Typography variant="body2">{formatExclusionReason(reason)}</Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', whiteSpace: 'normal' }}
                      >
                        {DocumentDataExclusionReasonDescriptions[reason]}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControlStyled>
          </DialogContentText>
        </DialogContentGlynt>
        <DialogActionsGlynt
          submitButtonText="Exclude"
          closeModal={handleCloseExcludeDialog}
          handleSubmit={handleConfirmExclude}
          cancelCypressId="btn-exclude-document-data-cancel"
          submitCypressId="btn-exclude-document-data-submit"
          clearCypressId="btn-exclude-document-data-clear"
          submitDisabled={isLoading || !exclusionReason}
        />
      </DialogGlynt>
    </>
  );
};

export default DocumentDataStatusChange;
